import { ChevronDown } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { bookingFees } from 'v5/core/booking-billing/fees';
import type { RateFormValue } from 'v5/core/entity-values/values.types';
import { formatMoneyAmount, spokenMoney } from 'v5/core/money/formatMoney';
import { rateRules } from 'v5/core/rates/rateRules';
import { feePercentsOf, RATE_FEES } from 'v5/core/rates/resourceRate';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { cn } from 'v5/platform/dom/cn';
import { useRateCopy } from './useRateCopy';

/**
 * One unit of the rate with its fees applied, worked out exactly as a
 * booking does (`bookingFees.apply`): agency on the rate, payroll on the
 * rate minus agency, processing on the subtotal. Nobody has to do cascade
 * math in their head — 15% + 3% on 850 is 999.18, not 1,003.
 */
export function RateWithFees({ path }: { path: string }) {
  const { t } = useTranslation('core');
  const copy = useRateCopy();
  const value = useWatch<Record<string, RateFormValue>>({ name: path });

  if (
    value.amount === null ||
    rateRules.amount(value.amount, value.currency).type !== 'valid' ||
    !RATE_FEES.every(fee => rateRules.fee(value[fee.column]).type === 'valid')
  ) {
    return null;
  }
  const fees = feePercentsOf(value);
  if (fees.agency + fees.payroll + fees.processing === 0) return null;

  // Each line rounds to cents on its own, like the booking cost summary.
  const breakdown = bookingFees.apply(value.amount, fees);
  const money = (amount: number) =>
    formatMoneyAmount(amount, copy.locale, {
      fractionDigits: { currency: value.currency }
    });
  const unit = copy.unitShort(value.interval);
  const total = money(breakdown.total);

  return (
    <details
      data-testid="rate-with-fees"
      className="group rounded-md border border-border-default bg-bg-subtle/60 text-sm"
    >
      <summary
        className={cn(
          'flex cursor-pointer list-none flex-wrap items-center gap-x-2 gap-y-1 rounded-md px-3 py-1.5 [&::-webkit-details-marker]:hidden',
          focusRings.focus.visible
        )}
      >
        <span aria-hidden="true" className="contents">
          <span className="text-fg-muted">{t('rateEditor.withFees')}</span>
          <span className="font-semibold tabular-nums text-fg-default">
            {value.currency} {total}
          </span>
          <span className="text-fg-muted">{unit}</span>
        </span>
        <span className="sr-only">
          {t('rateEditor.withFees')}:{' '}
          {spokenMoney(breakdown.total, value.currency, copy.locale)}{' '}
          {copy.unitSpoken(value.interval)}.
        </span>
        <span className="ml-auto inline-flex items-center gap-0.5 font-semibold text-fg-accent">
          {t('rateEditor.howCalculated')}
          <ChevronDown
            aria-hidden="true"
            className="size-4 transition-transform group-open:rotate-180"
          />
        </span>
      </summary>
      <table className="mb-1.5 w-full tabular-nums">
        <tbody className="[&_td]:px-3 [&_td]:py-0.5 [&_td:last-child]:text-right [&_td:last-child]:whitespace-nowrap">
          <tr>
            <td>{t('rateEditor.breakdown.rate')}</td>
            <td>{money(breakdown.rateTotal)}</td>
          </tr>
          {fees.agency > 0 && (
            <tr>
              <td>
                {t('rateEditor.breakdown.agency', { percent: fees.agency })}
              </td>
              <td>+ {money(breakdown.agencyFeeTotal)}</td>
            </tr>
          )}
          {fees.payroll > 0 && (
            <tr>
              <td>
                {t('rateEditor.breakdown.payroll', {
                  percent: fees.payroll,
                  base: money(breakdown.rateTotal - breakdown.agencyFeeTotal)
                })}
              </td>
              <td>+ {money(breakdown.payrollFeeTotal)}</td>
            </tr>
          )}
          {fees.processing > 0 && (
            <tr>
              <td>
                {t('rateEditor.breakdown.processing', {
                  percent: fees.processing,
                  base: money(breakdown.subtotalBeforeProcessing)
                })}
              </td>
              <td>+ {money(breakdown.processingFeeTotal)}</td>
            </tr>
          )}
          <tr className="border-t border-border-default font-semibold text-fg-default [&_td]:pt-1">
            <td>
              {t('rateEditor.breakdown.total')} {unit}
            </td>
            <td>
              {value.currency} {total}
            </td>
          </tr>
        </tbody>
      </table>
    </details>
  );
}
